import { Form } from "@unform/web";
import { useEffect, useRef, useState } from "react";

import { HTTPErrorCallback } from "Helpers/Error";
import { showNotification } from "Helpers/Toast";

import UseAuth from "Hooks/UseAuth";
import UseFile from "Hooks/UseFile";
import { UseGlobalLoading } from "Hooks/UseGlobalLoading";
import UseServices from "Hooks/UseServices";

import Button from "Components/Admin/Buttons";
import Input from "Components/Admin/Form/Input";
// import ConversationForm from "../ConversationForm";
import NotifyCounterpartButtons from "Components/Admin/NotifyCounterpartButtons";
import GetIcon from "Components/Global/Icons";

import * as S from "./styles";

import { useTheme } from "styled-components";

const ProjectConversationMessages = ({
  projectId,
  conversation,
  setConversation,
  onModerated,
}) => {
  const theme = useTheme();

  const { parsedJWT } = UseAuth();
  const { api } = UseServices();
  const { loading, setLoading } = UseGlobalLoading();
  const { uploadFile } = UseFile();

  const inputRef = useRef(null);

  const conversationRef = useRef(null);

  const [text, setText] = useState("");
  const [file, setFile] = useState(null);

  const [messages, setMessages] = useState([]);

  useEffect(() => {
    getConversationMessages(projectId);
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    if (messages.length && conversationRef.current)
      conversationRef.current.scrollTop = conversationRef.current.scrollHeight;
  }, [messages]);

  const getConversationMessages = (project_id) => {
    api
      .post("/projects/messages", {
        project_id: project_id,
        conversation_id: conversation.id,
      })
      .then((response) => {
        const messages = response.data?.messages;

        setMessages(messages);
      })
      .catch((error) => {
        setLoading(false);
        return HTTPErrorCallback(error);
      });
  };

  const sendMessage = async () => {
    if (!text) return;

    try {
      let file_src = null;

      if (file) {
        const location = await uploadFile(file);
        file_src = location;
      }

      await api.post("/projects/messages/create", {
        text: text,
        file_name: file?.name,
        file_src,
        project_id: projectId,
        conversation_id: conversation.id,
      });

      getConversationMessages(projectId);

      setText("");
      setFile(null);
    } catch (error) {
      setLoading(false);
      HTTPErrorCallback(error);
    }
  };

  const handleFileUpload = (e) => {
    const file = e.target.files[0];

    if (!file) return;

    if (file.size > 30000000) {
      return showNotification(
        "danger",
        "Atenção",
        "O arquivo deve conter no máximo 30 MB.",
      );
    }

    setFile(file);
  };

  // Workflow D23 (29/08/2026): o admin LIBERA a mensagem pendente
  // (visibilidade) e, se ela tem arquivo, AVALIA o arquivo depois.
  const releaseMessage = async (message_id) => {
    try {
      await api.post("/projects/messages/release", { message_id });

      getConversationMessages(projectId);

      // Liberou/avaliou: o badge da aba precisa cair na hora (05/09/2026).
      if (onModerated) onModerated();
    } catch (error) {
      setLoading(false);
      HTTPErrorCallback(error);
    }
  };

  const evaluateMessage = async (message_id, approved) => {
    try {
      await api.post("/projects/messages/evaluate", { message_id, approved });

      getConversationMessages(projectId);

      // Liberou/avaliou: o badge da aba precisa cair na hora (05/09/2026).
      if (onModerated) onModerated();
    } catch (error) {
      setLoading(false);
      HTTPErrorCallback(error);
    }
  };

  const main_user_id = conversation?.user_id;
  const is_my_conversation = conversation?.is_my_conversation;

  const target_user = conversation?.users.find(
    (user) => user.id === main_user_id,
  );

  return (
    <S.Wrapper>
      <Form>
        <S.Buttons>
          <Button
            type="button"
            text="Voltar"
            onClick={() => setConversation(null)}
            outline
            disabled={loading}
          />
        </S.Buttons>

        <S.Section>
          <span>Mensagens - {target_user?.full_name}</span>
          <hr />
        </S.Section>

        <S.Messages ref={conversationRef}>
          {!messages.length ? <span>Nenhuma mensagem encontrada.</span> : null}

          {messages.map((message) => (
            <S.Message
              key={message.id}
              isOwn={
                is_my_conversation
                  ? parsedJWT?.user?.id === message.user.id
                  : main_user_id === message.user_id
              }
              active={message.status !== "Reprovado"}
            >
              <S.MessageInfos>
                <h2>{message.user.full_name}</h2>
                <p>{message.text}</p>

                {message?.file_src ? (
                  <S.MessageFile
                    href={message.file_src}
                    target="_blank"
                    rel="noreferrer"
                  >
                    {message.file_name}
                  </S.MessageFile>
                ) : null}
              </S.MessageInfos>

              {message.status !== "Enviado" ? (
                <>
                  <hr />
                  <S.MessageStatus status={message.status}>
                    {message.status}
                  </S.MessageStatus>

                  {/* Workflow D23: Pendente → liberar; com arquivo, a
                      liberação leva a "Pendente de aprovação", onde admin ou
                      patrocinador avaliam o arquivo. */}
                  {message.status === "Pendente" ? (
                    <S.AuditButtons>
                      <Button
                        variant="success"
                        type="button"
                        text="Liberar"
                        onClick={() => releaseMessage(message.id)}
                        disabled={loading}
                      />
                    </S.AuditButtons>
                  ) : null}

                  {message.status === "Pendente de aprovação" ? (
                    <S.AuditButtons>
                      <Button
                        variant="critical"
                        type="button"
                        text="Reprovar"
                        onClick={() => evaluateMessage(message.id, false)}
                        disabled={loading}
                      />

                      <Button
                        variant="success"
                        type="button"
                        text="Aprovar"
                        onClick={() => evaluateMessage(message.id, true)}
                        disabled={loading}
                      />
                    </S.AuditButtons>
                  ) : null}

                  {/* Aviso MANUAL às pontas (D4 da parte 4): só na mensagem
                      aprovada, e sempre com confirmação — é e-mail para
                      terceiro. */}
                  <NotifyCounterpartButtons
                    messageId={message.id}
                    status={message.status}
                  />
                </>
              ) : null}
            </S.Message>
          ))}
        </S.Messages>

        {is_my_conversation ? (
          <>
            <Input
              type="textarea"
              name="text"
              placeholder="Digite sua mensagem"
              value={text}
              onChange={(e) => setText(e.target.value)}
              disabled={loading}
              height="100px"
            />

            <S.Buttons>
              {file ? (
                <S.File>
                  <span>{file.name}</span>
                  <GetIcon
                    name="X"
                    onClick={() => setFile(null)}
                    color={theme.colors.primary.vividCerulean}
                  />
                </S.File>
              ) : (
                <Button
                  type="button"
                  text="Adicionar Anexo"
                  onClick={() => inputRef.current.click()}
                  icon="Upload"
                  disabled={loading}
                  outline
                />
              )}
              <Button
                type="button"
                text="Enviar"
                onClick={() => sendMessage()}
                disabled={loading}
              />
            </S.Buttons>

            <S.HiddenInput
              ref={inputRef}
              type="file"
              name="file"
              onChange={handleFileUpload}
            />
          </>
        ) : null}
      </Form>
    </S.Wrapper>
  );
};

export default ProjectConversationMessages;
